import React, { useEffect, useState } from "react";
import LayoutEntrance from "../../LayoutEntrance";
import { Checkbox, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import mentee from "@images/logo.png";
import axios from "@MyAxios";
import { LINK_SENT, SIGNUP } from "@constants/api";
import { toast } from "react-toastify";
import MyAvatar from "@components/admin/MyAvatar";
import Button from "../../components/Button";
import { useNavigate } from "react-router-dom";
import Loader from "@components/Loader";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  border: {
    borderWidth: 1,
    borderColor: "#887E7E",
  },
  textColor: {
    color: theme.palette.primary.main,
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  fontSmall: {
    fontSize: "10px",
  },
  bottomImg: {
    position: "fixed",
    bottom: 0,
    right: 0,
    left: 0,
  },
}));

// The default community that is always pre-selected and cannot be deselected
const DEFAULT_COMMUNITY_NAME = "Serkohls";

const SelectSignupCommunities = () => {
  const classes = useStyles();
  const navigation = useNavigate();

  const [loading, setLoading] = useState(true);
  const [communities, setCommunities] = useState([]);
  const [communityInvitations, setCommunityInvitations] = useState([]);
  const [selectedCommunities, setSelectedCommunities] = useState([]);

  const handleSubmitCommunities = async () => {
    const parsedFormData = JSON.parse(localStorage.formData);

    if (!selectedCommunities.length)
      return toast.error("Please select at least one community");

    let profileData = {
      userName: parsedFormData.userName,
      phoneNumber: parsedFormData.phone,
      firstName: parsedFormData.firstName,
      lastName: parsedFormData.lastName,
      aliasOne: parsedFormData.aliasOne,
      aliasTwo: parsedFormData.aliasTwo,
      emailAddress: parsedFormData.email,
      age: parsedFormData.age,
      dateOfBirth: parsedFormData.dateOfBirth,
      country: parsedFormData.country,
      state: parsedFormData.state,
      city: parsedFormData.city || "",
      timezone: parsedFormData.timezone,
      password: parsedFormData.password,
      selectedCommunities,
    };

    try {
      profileData.age = dayjs().diff(dayjs(profileData.dateOfBirth, 'MM/DD/YYYY'), 'year');
      const res = await axios.post(SIGNUP, profileData);
      localStorage.removeItem("formData");
      navigation(LINK_SENT);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectCommunity = (community) => {
    if (Boolean(selectedCommunities.find((comId) => comId === community.id))) {
      if (
        Boolean(
          communityInvitations.find(
            (comInv) => comInv.community.id === community.id,
          ),
        ) &&
        !window.confirm(
          "You have been invited to join this community. Are you sure you want to reject?",
        )
      )
        return;

      setSelectedCommunities(
        selectedCommunities.filter((comId) => comId !== community.id),
      );
    } else {
      setSelectedCommunities([...selectedCommunities, community.id]);
    }
  };

  const fetchCommunities = async () => {
    const parsedFormData = JSON.parse(localStorage.formData);

    setLoading(true);
    axios
      .get("/get/all-communities", { params: { email: parsedFormData.email } })
      .then((res) => {
        const allCommunities = res.data.communities;
        const invitations = res.data.communityInvitations;

        setCommunities(allCommunities);
        setCommunityInvitations(invitations);

        // Always pre-select: Serkohls (mandatory) + any invitation communities
        const autoSelected = allCommunities
          .filter(
            (com) =>
              com.name?.trim().toLowerCase() === DEFAULT_COMMUNITY_NAME.toLowerCase() ||
              Boolean(
                invitations.find((comInv) => comInv.community.id === com.id),
              ),
          )
          .map((com) => com.id);

        // If somehow Serkohls isn't in the list, fall back to first community
        setSelectedCommunities(
          autoSelected.length > 0
            ? autoSelected
            : [allCommunities[0]?.id].filter(Boolean),
        );
      })
      .catch((e) => {
        console.log(e);
        toast.error(
          "Error fetching communities. Please try refreshing the page",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!localStorage.formData) {
      navigation("/username");
      return;
    }

    fetchCommunities();
  }, []);

  // If selectable communities are greater than 1
  const communitySelectionEnabled = communities?.length > 1;

  return (
    <LayoutEntrance>
      <Typography
        variant="h4"
        className={clsx(classes.textColor, "text-center font-extrabold mt-4")}
      >
        Sign Up for
      </Typography>
      <img
        src={mentee}
        alt="mentee"
        className="mx-auto mb-5"
        style={{ height: "30px" }}
      />

      {loading ? (
        <Loader />
      ) : !communitySelectionEnabled ? (
        <Typography variant="inherit" className="text-center mt-4 text-lg">
          You're all set!
        </Typography>
      ) : (
        <>
          <Typography variant="inherit" className="mb-5">
            Select the communities that interest you!
          </Typography>

          {Boolean(communityInvitations?.length) && (
            <Typography variant="inherit" className="mb-5">
              <strong>Note:</strong> You have been invited to join{" "}
              {communityInvitations.length === 1 ? "community" : "communities"}{" "}
              {communityInvitations
                ?.map((comInv) => comInv.community.name)
                .join(", ")}
            </Typography>
          )}

          <div
            className="overflow-y-auto"
            style={{
              maxHeight: Boolean(communityInvitations?.length)
                ? "55vh"
                : "65vh",
            }}
          >
            {communities?.map((com, key) => {
              const isChecked = Boolean(
                selectedCommunities.find((comId) => comId === com.id),
              );

              return (
                <div
                  key={key}
                  className="flex justify-between items-center mb-3"
                >
                  <MyAvatar
                    src={com?.icon?.currBucketUrl}
                    name={com?.name}
                    subtext={<span>{com?.description}</span>}
                  />
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleSelectCommunity(com)}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="mt-8">
        <Button disabled={loading} onClick={handleSubmitCommunities}>
          Start your Serkohls
        </Button>
      </div>
    </LayoutEntrance>
  );
};

export default SelectSignupCommunities;
